import { useMemo, type ReactNode } from "react";
import { Header, type HeaderProps } from "./atoms";
import { cn } from "../../../lib/utils";

/**
 * Props for the TimelineInteractiveCard component.
 * Extends HeaderProps to include all header-related properties.
 */
export interface TimelineInteractiveCardProps extends HeaderProps {
  /**
   * Optional content to display in the expanded card body.
   * When provided, the card shows an expanded state with this content.
   * When undefined, only the header with preview tooltip is shown.
   */
  children?: ReactNode;
}

/**
 * An interactive card component for displaying timeline items with expandable content.
 *
 * This component provides a card-based UI for timeline entries that can display:
 * - A header with icon, title, timestamp, and action menu
 * - Optional preview tooltip when hovering over collapsed cards
 * - Expandable content area when children are provided
 * - Tags and metadata in the preview tooltip
 *
 * The card automatically adapts its appearance based on whether content is expanded:
 * - Collapsed state: Shows header with hover preview tooltip
 * - Expanded state: Shows header with highlighted background and content area below
 *
 * @example
 * ```tsx
 * // Collapsed card with preview tooltip
 * <TimelineInteractiveCard
 *   icon="phone_call"
 *   title="Call with John"
 *   timestamp={new Date()}
 *   preview={{
 *     content: "Discussed project timeline",
 *     recentActivity: "2 hours ago"
 *   }}
 *   tags={[{ id: "1", name: "Important", color: "#FF0000" }]}
 *   actions={[
 *     { icon: EditIcon, label: "Edit", onClick: handleEdit }
 *   ]}
 * />
 *
 * // Expanded card with content
 * <TimelineInteractiveCard
 *   icon="note"
 *   title="Meeting Notes"
 *   timestamp={new Date()}
 *   actions={[
 *     { icon: DeleteIcon, label: "Delete", onClick: handleDelete }
 *   ]}
 * >
 *   <p>Detailed meeting notes go here...</p>
 * </TimelineInteractiveCard>
 * ```
 */
export const TimelineInteractiveCard = ({
  children,
  className,
  preview,
  ...headerProps
}: TimelineInteractiveCardProps) => {
  const hasChildren = useMemo(() => {
    if (Array.isArray(children)) {
      return children.some((child) => !!child);
    }
    return !!children;
  }, [children]);

  return (
    <div
      className={cn(
        "w-full rounded-[14px] bg-white border border-gray-200 hover:shadow-sm transition-shadow overflow-hidden",
        className
      )}
      data-testid="timeline-interactive-card"
    >
      <Header
        {...headerProps}
        preview={hasChildren ? undefined : preview}
        className={`transition-all ${hasChildren ? "bg-muted border-b border-gray-200" : undefined}`}
      />
      <div className={`transition-all ${hasChildren ? "opacity-100" : "h-0 opacity-0"}`}>{children}</div>
    </div>
  );
};
